import R from 'app/assets/R'
import {
  RenderValueTable,
  useHookTableManagement,
} from 'app/components/tables/BaseTableManagement/hook'
import { convertColumnTable } from 'app/components/tables/BaseTableReport/hook'
import { IDataBaseTable } from 'app/components/tables/BaseTableReport/type'
import { isEmpty, isEqual } from 'lodash'
import { Pagination } from 'parkway-web-common'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IGeneralWarehouseManagementTable } from './type'

import {
  requestExportWarehouseManagement,
  requestGetWarehouseManagement,
} from 'app/api/inventory-new'
import { MessageCode } from 'app/api/inventory-new/constant'
import {
  IParamsGetWarehouseManagement,
  IResponseGetWarehouseManagement,
} from 'app/api/inventory-new/model/management'
import { IMAGE_ACTION } from 'app/common/config'
import { DefineStatus, DefineStatusColor } from 'app/common/constants'
import {
  FunctionPermissionEnum,
  ModulePermissionEnum,
} from 'app/common/permission-module'
import { useVerifyPermissionAndRedirect } from 'app/common/use-verify-permission-redirect'
import { BaseCol } from 'app/components/common/BaseCol'
import { ActionsDropdown } from 'app/components/tables/BaseTableManagement/ActionDropdown'
import { ValueTableWrapper } from 'app/components/tables/BaseTableManagement/styles'
import { initPagination } from 'app/constant'
import { notificationController } from 'app/controllers/notification-controller'
import { Delete } from './components/Delete'
import {
  IResponseFilter,
  initResponseFilterTableManagement,
} from './components/FilterManagementTable/type'
import { Update } from './components/Update'
import { StatusButton } from './styles'

export const useHookTable = () => {
  const { t } = useTranslation()

  const [filter, setFilter] = useState<IResponseFilter>(
    initResponseFilterTableManagement,
  )

  const { isHavePermissionFunctionAndGoBack } = useVerifyPermissionAndRedirect()

  useEffect(() => {
    isHavePermissionFunctionAndGoBack(
      ModulePermissionEnum.INVENTORY,
      FunctionPermissionEnum.WAREHOUSE_READ,
      R.strings.role,
    )
  }, [])

  const onClickExport = async params => {
    try {
      const response = await requestExportWarehouseManagement({
        keyword: params?.search ?? filter?.search,
      })
      const url = `${IMAGE_ACTION.IMAGE_SERVER}/${response?.data?.Url}`
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (error) {
      // console.log({ error })
      notificationController.error({
        message: t(R.strings.error),
      })
    }
  }
  const getDataTable = async (
    pagination: Pagination,
    newFilter?: IResponseFilter,
  ) => {
    const initData = {
      pagination: pagination ?? initPagination,
      data: [],
    } as IDataBaseTable<IGeneralWarehouseManagementTable>
    try {
      const status = newFilter?.status ?? filter?.status
      let params: IParamsGetWarehouseManagement = {
        page: pagination?.current,
        pagesize: pagination?.pageSize,
        keyword: newFilter?.search ?? filter?.search,
      }

      params = { ...params, ...newFilter, status }

      const response: IResponseGetWarehouseManagement =
        await requestGetWarehouseManagement(params)

      if (isEqual(response?.msgcode, MessageCode.successGetData)) {
        const data = response?.data
        const metadata = response?.metadata

        const startIndex =
          ((pagination?.current ?? 1) - 1) * (pagination?.pageSize ?? 10)

        const transformData = data => {
          if (isEmpty(data)) {
            return data
          }

          return data?.map((item, index) => {
            return {
              key: `${item?._id}-${index + 1}`,
              ordinalNumber: startIndex + index + 1,
              code: item?.code ?? '-',
              name: item?.name ?? '-',
              roadWarehouseCode: item?.roadWarehouseCode ?? '-',
              unit: item?.unit ?? '-',
              unitId: item?.unitId ?? '-',
              status: item?.status,
              _id: item?._id ?? '-',
            }
          })
        }

        const dataTables = transformData(data)

        return {
          pagination: {
            current: metadata?.currentPage ?? 1,
            pageSize: pagination?.pageSize,
            total: metadata?.totalItem ?? 0,
          },
          data: dataTables,
        } as IDataBaseTable<IGeneralWarehouseManagementTable>
      }

      return initData
    } catch (error) {
      return initData
    }
  }

  const { tableData, selectRowsTable, fetch, rowSelection } =
    useHookTableManagement({ getDataTable })

  const handleTableChange = pagination => {
    fetch(pagination, filter)
  }

  const handleChangeFilter = (newFilter: IResponseFilter) => {
    if (!isEqual(newFilter, filter)) {
      setFilter(newFilter)
      fetch(
        {
          ...tableData.pagination,
          current: initPagination.current,
        },
        newFilter,
      )
    }
  }

  const fetchNewData = () => {
    fetch(initPagination)
    setFilter(initResponseFilterTableManagement)
  }

  const columns = useMemo(() => {
    const option = [
      convertColumnTable<IGeneralWarehouseManagementTable>({
        title: '#',
        key: 'ordinalNumber',
        classNameWidthColumnOverwrite: 'number-column',
        render: (_, record) => (
          <RenderValueTable value={record.ordinalNumber} type="Base" />
        ),
      }),

      convertColumnTable<IGeneralWarehouseManagementTable>({
        title: t(R.strings.warehouse_code),
        key: 'code',
        classNameWidthColumnOverwrite: 'normal-column',
        render: (_, record) => (
          <RenderValueTable value={record?.code} type="Base" />
        ),
      }),

      convertColumnTable<IGeneralWarehouseManagementTable>({
        title: t(R.strings.warehouse_road_code),
        key: 'roadWarehouseCode',
        classNameWidthColumnOverwrite: 'normal-column',
        render: (_, record) => (
          <RenderValueTable value={record?.roadWarehouseCode} type="Base" />
        ),
      }),

      convertColumnTable<IGeneralWarehouseManagementTable>({
        title: t(R.strings.name),
        key: 'name',
        classNameWidthColumnOverwrite: 'very-big-column',
        render: (_, record) => (
          <RenderValueTable value={record?.name} type="Main" />
        ),
      }),

      convertColumnTable<IGeneralWarehouseManagementTable>({
        title: t(R.strings.branch),
        key: 'unit',
        classNameWidthColumnOverwrite: 'big-column',
        render: (_, record) => (
          <RenderValueTable value={record?.unit?.name} type="Base" />
        ),
      }),
      convertColumnTable<IGeneralWarehouseManagementTable>({
        title: t(R.strings.status),
        key: 'status',
        classNameWidthColumnOverwrite: 'normal-column',
        render: (_, record) => {
          const statusColor = DefineStatusColor[Number(record?.status)]

          return (
            <StatusButton
              $color={statusColor?.color}
              $background={statusColor?.background}
            >
              {t(DefineStatus[Number(record?.status)])}
            </StatusButton>
          )
        },
      }),

      convertColumnTable<IGeneralWarehouseManagementTable>({
        title: '',
        key: 'action',
        fixed: 'right',
        classNameWidthColumnOverwrite: 'small-column',
        render: (_, record) => (
          <ValueTableWrapper
            justify={'end'}
            align={'middle'}
            gutter={[10, 10]}
            wrap={false}
          >
            <BaseCol>
              <ActionsDropdown
                actionComponent={
                  <>
                    <Update warehouse={record} fetchTable={fetchNewData} />
                    <Delete
                      id={record?._id}
                      fetchTable={fetchNewData}
                      name={record?.name}
                    />
                  </>
                }
              />
            </BaseCol>
          </ValueTableWrapper>
        ),
      }),
    ]
    return option
  }, [t, fetchNewData])

  return {
    tableData,
    selectRowsTable,
    handleTableChange,
    fetchNewData,
    rowSelection,
    columns,
    handleChangeFilter,
    filter,
    onClickExport,
  }
}
